import { Assignee } from '../../../shared/interfaces/company';

export class LeadsCriteria {
  public readonly id!: number;
  public readonly created_by!: Assignee;
  public updated_by!: Assignee;
  public readonly name!: string;
  public readonly description: string | null = null;
  public readonly criteria!: LeadsCriteriaValues;
  public readonly is_active!: boolean;
  public readonly deprecated!: boolean;
  public readonly created_at_utc!: Date;
  public updated_at_utc!: Date;
  public is_matching_in_progress!: boolean;
}

export interface LeadsCriteriaValues {
  preferred_languages: string[];
  nationwide_support?: boolean;
  states: string[];
  counties?: string[];
  cities?: string[];
  zip_codes?: string[];
  supports: string[];
  is_military_veteran_prog: boolean;
  is_veteran_spouse_prog: boolean;
  need_business_entity?: boolean;
  loans_and_capital?: {
    need_state_registration?: boolean;
    loan_types?: string[];
    loan_size_range?: RangeValue;
    financial_documentations?: string[];
    credit_score_range?: string;
    capital_deployment_areas?: string[];
    cosigner_helps?: string;
    need_business_plan?: string;
    need_financial_projections?: string;
  };
  technical_assistance?: {
    has_target_industries?: boolean;
    target_industries?: string[];
    support_areas?: string[];
    offers_employee_ownership?: boolean;
    business_help_areas?: string[];
    ta_programs_paid_service?: string;
    paid_support_areas?: string[];
  };
  business_operational_tools?: {
    tools?: {
      business_banking: string[];
      marketing_help: string[];
      tech_support: string[];
      business_insurance: string[];
      new_technology: string[];
    };
  };
  commercial_real_estate?: {
    cre_help_locate_facilities?: boolean;
    cre_upgrade_facility?: boolean;
    cre_need_own_facility?: boolean;
    cre_work_with_tenants?: boolean;
    cre_solar_energy_upgrades?: boolean;
  };
  solar_and_energy?: {
    solar_lending_products?: boolean;
    solar_loan_amount_range?: RangeValue;
    solar_need_credit_score?: boolean;
    solar_credit_score_ranges?: string;
    solar_loan_requirements?: string;
    offers_energy_efficiency?: boolean;
    offers_battery_lending?: boolean;
    battery_loan_amount_range?: RangeValue;
    battery_need_credit_score?: boolean;
    battery_credit_score_ranges?: string;
    battery_loan_requirements?: string;
    need_power_use_statement?: boolean;
    offers_ev_products?: boolean;
    ev_loan_amount_range?: RangeValue;
    ev_need_credit_score?: boolean;
    ev_credit_score_ranges?: string;
    ev_loan_requirements?: string;
  };
  finding_investors?: {
    investment_types?: string[];
    require_regional_investment?: boolean;
  };
  mandatory_match_fields?: string[];
}

export interface RangeValue {
  min: number;
  max: number | null;
}

export const SUPPORTED_LANGUAGES = ['English', 'Spanish'] as const;

export const STATE_OPTIONS = [
  'North Carolina',
  'South Carolina',
  'Georgia',
  'Alabama',
  'Tennessee',
  'Pennsylvania',
  'Virginia',
  'West Virginia',
  'Ohio',
  'New York',
  'Kentucky',
  'Maryland',
  'Mississippi',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'Florida',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Louisiana',
  'Maine',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'North Dakota',
  'Oklahoma',
  'Oregon',
  'Rhode Island',
  'South Dakota',
  'Texas',
  'Utah',
  'Vermont',
  'Washington',
  'Wisconsin',
  'Wyoming',
  'Nationwide',
] as const;

export const BUSINESS_SUPPORT_OPTIONS = [
  'Loans and Capital Products',
  'Small Business Technical Assistance',
  'Business Operational Tools',
  'Commercial Real Estate',
  'Solar and Energy Efficiency Products',
  'Investing in Companies',
  'Chamber of Commerce Membership or Regional Networking Group',
] as const;

export const BUSINESS_SUPPORT_KEY_TO_DISPLAY_NAME: Record<string, string> = {
  loans_and_capital: 'Loans and Capital Products',
  technical_assistance: 'Small Business Technical Assistance',
  business_operational_tools: 'Business Operational Tools',
  commercial_real_estate: 'Commercial Real Estate',
  solar_and_energy: 'Solar and Energy Efficiency Products',
  finding_investors: 'Investing in Companies',
};

export const LOAN_TYPE_OPTIONS = [
  'Term Loans',
  'Working Capital Loans',
  // 'SBA Loan Product',
  'Loan to purchase or upgrade a business facility or equipment',
  'Refinance other Debt',
  'Real Estate Lending (residential)',
  'Commercial Real Estate Purchase',
  'Start-up Support',
  'Short-term quick capital',
  'Business Acquisition',
  'Grant Programs',
  'Venture Funding (Debt, equity, or Revenue Share)',
  'Acquisition / M&A',
  'Project Finance',
] as const;

export const FINANCIAL_DOC_OPTIONS = [
  'Audited financial statements',
  'Unaudited formal financial statements',
  'Informal financial statements',
  'Tax returns',
  'No financial statements available',
  'Bank Statements',
] as const;

export const CREDIT_SCORE_OPTIONS = [
  'No Credit score required',
  'Below 580',
  '580 and above',
  '670 and above',
  '740 and above',
  '800 and above',
] as const;

export const AREA_TYPE_OPTIONS = [
  'Opportunity Zones',
  'New Market Tax Credit Area',
  'Low-to-moderate Income Areas',
  'CDFI Investment Areas',
  'Reservations',
  'USDA Rural defined areas',
] as const;

export const INDUSTRY_OPTIONS = [
  { label: 'Agriculture & Farming (1111)', value: '1111' },
  { label: 'Construction (2361)', value: '2361' },
  { label: 'Manufacturing (3111)', value: '3111' },
  { label: 'Wholesale Trade (4234)', value: '4234' },
  { label: 'Retail Trade (4451)', value: '4451' },
  { label: 'Transportation & Logistics (4841)', value: '4841' },
  { label: 'Warehousing & Storage (4931)', value: '4931' },
  { label: 'Software & Technology (5112)', value: '5112' },
  { label: 'IT & Computer Services (5415)', value: '5415' },
  { label: 'Banking & Financial Services (5221)', value: '5221' },
  { label: 'Insurance (5241)', value: '5241' },
  { label: 'Real Estate (5311)', value: '5311' },
  { label: 'Professional & Consulting Services (5416)', value: '5416' },
  { label: 'Healthcare & Medical Services (6211)', value: '6211' },
  { label: 'Restaurants & Food Services (7225)', value: '7225' },
  { label: 'Hotels & Accommodation (7211)', value: '7211' },
  { label: 'Automotive Services (8111)', value: '8111' },
  { label: 'Personal Services (8121)', value: '8121' },
  { label: 'Government & Public Services (9281)', value: '9281' },
  { label: 'Educational Services (6111)', value: '6111' },
  { label: 'Mining (2121)', value: '2121' },
  { label: 'Utilities (2211)', value: '2211' },
  { label: 'Waste Management (5621)', value: '5621' },
] as const;

export const SUPPORT_AREA_OPTIONS = [
  'Marketing',
  'Finances/Accounting',
  'Strategy',
  'Legal Assistance/Questions',
  'Business Succession Planning',
  'Applying for a Grant',
  'Tax Structure/Tax support',
  'Site Selection',
  'Human Resources',
  'Operational Planning',
  'Applying for a loan or capital',
  'Banking Accounts/Systems',
  'Technology',
  'IT Infrastructure',
  'Investment Readiness Support',
  'Government Contracting',
  'Manufacturing Support',
] as const;

export const BUSINESS_HELP_AREA_OPTIONS = [
  'Creating a Business Plan',
  'Creating a Financial Forecast',
  'Understanding Business Structures',
  'Knowing what resources are available to support you',
] as const;

export const OPERATIONAL_TOOLS_OPTIONS = [
  'Open or manage business accounts (checking, savings, investments)',
  'Help with payroll and HR',
  'Get a line of credit',
  "Help managing your company's cash flow",
  'Advice on financial strategy',
  'Help designing your website or improving search engine visibility',
  'Help with flyers, signs, or other physical marketing',
  'Help planning or improving your marketing strategy',
  'Help with IT setup, troubleshooting, and online security',
  'Help setting up physical tech like servers, internet, etc.',
  'General business insurance',
  'Insurance for disasters (flood, fire, etc.)',
  'Property or building insurance',
  'Protection from lawsuits',
  'Insurance for key team members',
  'Coverage for directors or officers',
  'Help using new digital tools in your business',
] as const;

export const BUSINESS_AGE_OPTIONS = [
  '<1 year',
  '1-2 years',
  '2-3 years',
  '4+ years',
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export type StateOption = (typeof STATE_OPTIONS)[number];
export type BusinessSupportOption = (typeof BUSINESS_SUPPORT_OPTIONS)[number];
export type LoanTypeOption = (typeof LOAN_TYPE_OPTIONS)[number];
export type FinancialDocOption = (typeof FINANCIAL_DOC_OPTIONS)[number];
export type CreditScoreOption = (typeof CREDIT_SCORE_OPTIONS)[number];
export type AreaTypeOption = (typeof AREA_TYPE_OPTIONS)[number];
export type IndustryOption = (typeof INDUSTRY_OPTIONS)[number];
export type SupportAreaOption = (typeof SUPPORT_AREA_OPTIONS)[number];
export type BusinessHelpAreaOption =
  (typeof BUSINESS_HELP_AREA_OPTIONS)[number];
export type OperationalToolsOption = (typeof OPERATIONAL_TOOLS_OPTIONS)[number];
export type BusinessAgeOption = (typeof BUSINESS_AGE_OPTIONS)[number];
